<template>
    <div class="row">&nbsp;</div>
    <div class="xForm">
        <form @submit.prevent="handleSubmit">
            <div class="form-group">
                <label for="BusName" class="label">Business Name:</label>
                <input id="BusName" class="form-control" type="text" required v-model="BusName" />
            </div>
            <div class="form-group">
                <label for="codeDescription" class="label">Code Description:</label>
                <textarea id="codeDescription" class="form-control" rows="3" v-model="codeDescription"></textarea>
            </div>
            <div class="thisOr">
                <div class="formSectionLabel">All required for this style</div>
                <div class="form-group">
                    <div v-if="redirecturl" class="fieldPreview">{{ redirecturl }}</div>
                    <label for="redirecturl" class="label>">Redirect URL</label>
                    <input id="redirecturl" class="form-control" type="url" v-model="redirecturl"
                        placeholder="https://example.com"
                        pattern="[Hh][Tt][Tt][Pp][Ss]?:\/\/(?:(?:[a-zA-Z\u00a1-\uffff0-9]+-?)*[a-zA-Z\u00a1-\uffff0-9]+)(?:\.(?:[a-zA-Z\u00a1-\uffff0-9]+-?)*[a-zA-Z\u00a1-\uffff0-9]+)*(?:\.(?:[a-zA-Z\u00a1-\uffff]{2,}))(?::\d{2,5})?(?:\/[^\s]*)?" />
                    <RedirectSafetyStatus :state="redirectSafetyState" :tooltip="redirectSafetyTooltip" />
                </div>
            </div>
            <div class="form-group fatfinger">
                <span class="label">UPC (GTIN) Support (Optional)</span><br />
                <input id="upcSupport" class="form-check-input" type="checkbox" v-model="upcSupport"
                    default="false">&nbsp;
                <label for="upcSupport" class="label">UPC (GTIN) Support?</label>
            </div>
            <div class="thisOr" v-if="upcSupport">
                <div class="formSectionLabel">UPC Code Support (optional)</div>
                <div class="form-group">
                    <input id="upc" class="form-control" @keyup="checkUPC" @blur="syncGs1Ai01FromGtin" type="text" v-model="upc"
                        :placeholder="upcSuggestion" />
                    <label for="upc" class="label">UPC Code</label>
                    <div class="upcError">{{ upcError }}</div>
                    <div class="notes">
                        <small>Once 12 digits are entered the UPC barcode will be generated automatically when the code
                            is
                            saved. Correct check digit is required.</small>
                    </div>
                </div>
                <div class="form-group">
                    <input id="calcCheckDigit" class="form-check-input" type="checkbox" v-model="calcCheckDigit"
                        default="false">&nbsp;
                    <label for="calcCheckDigit" class="label">Calculate Check Digit?</label>
                    <div class="notes"><small>Type or paste in 11 digits and the form will calculate and insert the
                            final
                            check digit.</small>
                    </div>
                </div>
                <div class="form-group fatfinger">
                    <span class="label">GS1 2D Payloads (Optional)</span><br />
                    <input id="gs1Support" class="form-check-input" type="checkbox" v-model="gs1Support"
                        default="false">&nbsp;
                    <label for="gs1Support" class="label">GS1 2D Payloads?</label>
                </div>
                <div class="thisOr" v-if="gs1Support">
                    <div class="formSectionLabel">GS1 2D Payloads (optional)</div>
                    <div class="form-group">
                        <div class="labelWithHelp">
                            <label for="gs1DigitalLinkBaseUri" class="label">GS1 Digital Link Base URI</label>
                            <button
                                type="button"
                                class="helpLink"
                                :title="gs1DigitalLinkBaseUriHelpText"
                                :aria-label="gs1DigitalLinkBaseUriHelpText"
                            >?</button>
                        </div>
                        <input id="gs1DigitalLinkBaseUri" class="form-control" type="url" v-model="gs1DigitalLinkBaseUri"
                            placeholder="https://id.gs1.org/" />
                    </div>
                    <div class="form-group">
                        <div class="labelWithHelp">
                            <label class="label">GS1 Application Identifiers</label>
                            <a class="helpLink" href="https://ref.gs1.org/ai/?lang=en" target="_blank"
                                rel="noopener noreferrer" aria-label="Open GS1 AI reference">?</a>
                        </div>
                        <div v-for="(entry, index) in gs1AiEntries" :key="`gs1-ai-${index}`" class="gs1AiRow">
                            <input class="form-control gs1AiCode" type="text" inputmode="numeric" maxlength="4"
                                v-model="entry.ai" placeholder="AI" />
                            <input class="form-control gs1AiValue" type="text" v-model="entry.value"
                                :placeholder="entry.ai === '01' ? '09521234543213' : entry.ai === '10' ? 'ABC123' : entry.ai === '17' ? '260930' : 'Value'" />
                            <button v-if="!entry.isDefault" type="button" class="btn btn-outline-secondary btn-sm"
                                @click="removeGs1AiEntry(index)">Remove</button>
                        </div>
                        <button type="button" class="btn btn-secondary btn-sm" @click="addGs1AiEntry">Add AI</button>
                    </div>
                    <div class="form-group">
                        <label class="label">Derived GS1 Digital Link</label>
                        <textarea class="form-control jsonOptionsInput" rows="2" :value="derivedGs1DigitalLinkUri" readonly></textarea>
                    </div>
                    <div class="form-group">
                        <label class="label">Derived GS1 Element String</label>
                        <textarea class="form-control jsonOptionsInput" rows="3" :value="derivedGs1ElementString" readonly></textarea>
                    </div>
                </div>
            </div>
            <div class="form-group">
                <button type="button" class="btn btn-secondary btn-sm" @click="toggleAdvancedBarcodeOptions">
                    {{ showAdvancedBarcodeOptions ? 'Hide' : 'Show' }} Advanced Barcode Rendering Options
                </button>
            </div>
            <div class="thisOr" v-if="showAdvancedBarcodeOptions">
                <div class="formSectionLabel">Common Barcode Rendering Options</div>
                <div class="form-group">
                    <label class="label">QR Options</label>
                    <div class="optionsGrid">
                        <div>
                            <label for="qrWidth" class="label">Width</label>
                            <input id="qrWidth" class="form-control" type="number" min="1" v-model.number="qrWidth" />
                        </div>
                        <div>
                            <label for="qrColor" class="label">Color</label>
                            <input id="qrColor" class="form-control colorPicker" type="color"
                                :value="formatHexColor(qrColor)"
                                @input="updateColorField('qrColor', $event.target.value)" />
                        </div>
                        <div>
                            <label for="qrBackground" class="label">Background</label>
                            <input id="qrBackground" class="form-control colorPicker" type="color"
                                :value="formatHexColor(qrBackground)"
                                @input="updateColorField('qrBackground', $event.target.value)" />
                        </div>
                        <div>
                            <label for="qrErrorCorrectionLevel" class="label">Error Correction</label>
                            <select id="qrErrorCorrectionLevel" class="form-control" v-model="qrErrorCorrectionLevel">
                                <option value="L">L</option>
                                <option value="M">M</option>
                                <option value="Q">Q</option>
                                <option value="H">H</option>
                            </select>
                        </div>
                        <div>
                            <label for="qrBorder" class="label">Border</label>
                            <input id="qrBorder" class="form-control" type="number" min="0" v-model.number="qrBorder" />
                        </div>
                        <div>
                            <label for="qrBaseurl" class="label">Base URL</label>
                            <input id="qrBaseurl" class="form-control" type="text" v-model="qrBaseurl" />
                        </div>
                    </div>
                </div>
                <div class="form-group">
                    <label class="label">GTIN Options</label>
                    <div class="optionsGrid">
                        <div>
                            <label for="gtinFormat" class="label">Format</label>
                            <select id="gtinFormat" class="form-control" v-model="gtinFormat">
                                <option v-for="format in gtinFormats" :key="format" :value="format">{{ format }}</option>
                            </select>
                        </div>
                        <div>
                            <label for="gtinWidth" class="label">Width</label>
                            <input id="gtinWidth" class="form-control" type="number" min="1" v-model.number="gtinWidth" />
                        </div>
                        <div>
                            <label for="gtinHeight" class="label">Height</label>
                            <input id="gtinHeight" class="form-control" type="number" min="1" v-model.number="gtinHeight" />
                        </div>
                        <div>
                            <label for="gtinScale" class="label">Scale</label>
                            <input id="gtinScale" class="form-control" type="number" min="1" v-model.number="gtinScale" />
                        </div>
                        <div>
                            <label for="gtinIncludeText" class="label">Include Text</label>
                            <input id="gtinIncludeText" class="form-check-input" type="checkbox" v-model="gtinIncludeText" />
                        </div>
                        <div>
                            <label for="gtinTextsize" class="label">Text Size</label>
                            <input id="gtinTextsize" class="form-control" type="number" min="1" v-model.number="gtinTextsize" />
                        </div>
                        <div>
                            <label for="gtinColor" class="label">Color</label>
                            <input id="gtinColor" class="form-control colorPicker" type="color"
                                :value="formatHexColor(gtinColor)"
                                @input="updateColorField('gtinColor', $event.target.value)" />
                        </div>
                        <div>
                            <label for="gtinBackground" class="label">Background</label>
                            <input id="gtinBackground" class="form-control colorPicker" type="color"
                                :value="formatHexColor(gtinBackground)"
                                @input="updateColorField('gtinBackground', $event.target.value)" />
                        </div>
                    </div>
                </div>
                <div class="form-group">
                    <label class="label">Combined Barcode Options</label>
                    <div class="optionsGrid">
                        <div>
                            <label for="combinedFormat" class="label">Format</label>
                            <select id="combinedFormat" class="form-control" v-model="combinedFormat">
                                <option value="png">png</option>
                                <option value="jpeg">jpeg</option>
                                <option value="svg">svg</option>
                            </select>
                        </div>
                        <div>
                            <label for="combinedBaseurl" class="label">Base URL</label>
                            <input id="combinedBaseurl" class="form-control" type="text" v-model="combinedBaseurl" />
                        </div>
                        <div>
                            <label for="combinedGtinWidth" class="label">GTIN Width</label>
                            <input id="combinedGtinWidth" class="form-control" type="number" min="1" v-model.number="combinedGtinWidth" />
                        </div>
                        <div>
                            <label for="combinedGtinHeight" class="label">GTIN Height</label>
                            <input id="combinedGtinHeight" class="form-control" type="number" min="1" v-model.number="combinedGtinHeight" />
                        </div>
                        <div>
                            <label for="combinedGtinOrientation" class="label">GTIN Orientation</label>
                            <select id="combinedGtinOrientation" class="form-control" v-model="combinedGtinOrientation">
                                <option value="horizontal">horizontal</option>
                                <option value="vertical">vertical</option>
                            </select>
                        </div>
                        <div>
                            <label for="combinedGtinFormat" class="label">GTIN Format</label>
                            <select id="combinedGtinFormat" class="form-control" v-model="combinedGtinFormat">
                                <option v-for="format in gtinFormats" :key="`combined-${format}`" :value="format">{{ format }}</option>
                            </select>
                        </div>
                        <div>
                            <label for="combinedGtinScale" class="label">GTIN Scale</label>
                            <input id="combinedGtinScale" class="form-control" type="number" min="1" v-model.number="combinedGtinScale" />
                        </div>
                        <div>
                            <label for="combinedIncludeGtinText" class="label">Include GTIN Text</label>
                            <input id="combinedIncludeGtinText" class="form-check-input" type="checkbox" v-model="combinedIncludeGtinText" />
                        </div>
                        <div>
                            <label for="combinedGtinColor" class="label">GTIN Color</label>
                            <input id="combinedGtinColor" class="form-control colorPicker" type="color"
                                :value="formatHexColor(combinedGtinColor)"
                                @input="updateColorField('combinedGtinColor', $event.target.value)" />
                        </div>
                        <div>
                            <label for="combinedGtinBackground" class="label">GTIN Background</label>
                            <input id="combinedGtinBackground" class="form-control colorPicker" type="color"
                                :value="formatHexColor(combinedGtinBackground)"
                                @input="updateColorField('combinedGtinBackground', $event.target.value)" />
                        </div>
                        <div>
                            <label for="combinedQrSizeRatio" class="label">QR Size Ratio</label>
                            <input id="combinedQrSizeRatio" class="form-control" type="number" step="0.01" min="0" v-model.number="combinedQrSizeRatio" />
                        </div>
                        <div>
                            <label for="combinedQrHorizontalRatio" class="label">QR Horizontal Ratio</label>
                            <input id="combinedQrHorizontalRatio" class="form-control" type="number" step="0.01" min="0" v-model.number="combinedQrHorizontalRatio" />
                        </div>
                        <div>
                            <label for="combinedQrVerticalRatio" class="label">QR Vertical Ratio</label>
                            <input id="combinedQrVerticalRatio" class="form-control" type="number" step="0.01" min="0" v-model.number="combinedQrVerticalRatio" />
                        </div>
                        <div>
                            <label for="combinedQrColor" class="label">QR Color</label>
                            <input id="combinedQrColor" class="form-control colorPicker" type="color"
                                :value="formatHexColor(combinedQrColor)"
                                @input="updateColorField('combinedQrColor', $event.target.value)" />
                        </div>
                        <div>
                            <label for="combinedQrBackground" class="label">QR Background</label>
                            <input id="combinedQrBackground" class="form-control colorPicker" type="color"
                                :value="formatHexColor(combinedQrBackground)"
                                @input="updateColorField('combinedQrBackground', $event.target.value)" />
                        </div>
                        <div>
                            <label for="combinedQrErrorCorrectionLevel" class="label">QR Error Correction</label>
                            <select id="combinedQrErrorCorrectionLevel" class="form-control" v-model="combinedQrErrorCorrectionLevel">
                                <option value="L">L</option>
                                <option value="M">M</option>
                                <option value="Q">Q</option>
                                <option value="H">H</option>
                            </select>
                        </div>
                        <div>
                            <label for="combinedQrBorder" class="label">QR Border</label>
                            <input id="combinedQrBorder" class="form-control" type="number" min="0" v-model.number="combinedQrBorder" />
                        </div>
                    </div>
                </div>
                <div class="form-group" v-if="gs1Support">
                    <label class="label">GS1 QR Options</label>
                    <div class="optionsGrid">
                        <div>
                            <label for="gs1QrScale" class="label">Scale</label>
                            <input id="gs1QrScale" class="form-control" type="number" min="1" max="20" v-model.number="gs1QrScale" />
                        </div>
                        <div>
                            <label for="gs1QrIncludeText" class="label">Include Text</label>
                            <input id="gs1QrIncludeText" class="form-check-input" type="checkbox" v-model="gs1QrIncludeText" />
                        </div>
                        <div>
                            <label for="gs1QrTextsize" class="label">Text Size</label>
                            <input id="gs1QrTextsize" class="form-control" type="number" min="1" max="50" v-model.number="gs1QrTextsize" />
                        </div>
                        <div>
                            <label for="gs1QrColor" class="label">Color</label>
                            <input id="gs1QrColor" class="form-control colorPicker" type="color"
                                :value="formatHexColor(gs1QrColor)"
                                @input="updateColorField('gs1QrColor', $event.target.value)" />
                        </div>
                        <div>
                            <label for="gs1QrBackground" class="label">Background</label>
                            <input id="gs1QrBackground" class="form-control colorPicker" type="color"
                                :value="formatHexColor(gs1QrBackground)"
                                @input="updateColorField('gs1QrBackground', $event.target.value)" />
                        </div>
                    </div>
                </div>
                <div class="form-group" v-if="gs1Support">
                    <label class="label">GS1 DataMatrix Options</label>
                    <div class="optionsGrid">
                        <div>
                            <label for="gs1DataMatrixScale" class="label">Scale</label>
                            <input id="gs1DataMatrixScale" class="form-control" type="number" min="1" max="20" v-model.number="gs1DataMatrixScale" />
                        </div>
                        <div>
                            <label for="gs1DataMatrixIncludeText" class="label">Include Text</label>
                            <input id="gs1DataMatrixIncludeText" class="form-check-input" type="checkbox" v-model="gs1DataMatrixIncludeText" />
                        </div>
                        <div>
                            <label for="gs1DataMatrixTextsize" class="label">Text Size</label>
                            <input id="gs1DataMatrixTextsize" class="form-control" type="number" min="1" max="50" v-model.number="gs1DataMatrixTextsize" />
                        </div>
                        <div>
                            <label for="gs1DataMatrixColor" class="label">Color</label>
                            <input id="gs1DataMatrixColor" class="form-control colorPicker" type="color"
                                :value="formatHexColor(gs1DataMatrixColor)"
                                @input="updateColorField('gs1DataMatrixColor', $event.target.value)" />
                        </div>
                        <div>
                            <label for="gs1DataMatrixBackground" class="label">Background</label>
                            <input id="gs1DataMatrixBackground" class="form-control colorPicker" type="color"
                                :value="formatHexColor(gs1DataMatrixBackground)"
                                @input="updateColorField('gs1DataMatrixBackground', $event.target.value)" />
                        </div>
                        <div>
                            <label for="gs1DataMatrixRectangular" class="label">Rectangular</label>
                            <input id="gs1DataMatrixRectangular" class="form-check-input" type="checkbox" v-model="gs1DataMatrixRectangular" />
                        </div>
                    </div>
                </div>
            </div>
            <div class="thisOr" v-if="showAdvancedBarcodeOptions">
                <div class="formSectionLabel">Barcode Rendering Options (JSON)</div>
                <div class="form-group">
                    <input id="useAdvancedBarcodeJson" class="form-check-input" type="checkbox"
                        v-model="useAdvancedBarcodeJson" />
                    <label for="useAdvancedBarcodeJson" class="label">Use Advanced JSON Overrides On Save</label>
                </div>
                <div class="form-group">
                    <label for="qrOptionsJson" class="label">QR Options Object</label>
                    <textarea id="qrOptionsJson" class="form-control jsonOptionsInput" rows="6"
                        v-model="qrOptionsJson"></textarea>
                </div>
                <div class="form-group">
                    <label for="gtinOptionsJson" class="label">GTIN Options Object</label>
                    <textarea id="gtinOptionsJson" class="form-control jsonOptionsInput" rows="6"
                        v-model="gtinOptionsJson"></textarea>
                </div>
                <div class="form-group">
                    <label for="combinedBarcodeJson" class="label">Combined Barcode Object</label>
                    <textarea id="combinedBarcodeJson" class="form-control jsonOptionsInput" rows="6"
                        v-model="combinedBarcodeJson"></textarea>
                </div>
                <div class="form-group">
                    <label for="gs1QrOptionsJson" class="label">GS1 QR Options Object</label>
                    <textarea id="gs1QrOptionsJson" class="form-control jsonOptionsInput" rows="6"
                        v-model="gs1QrOptionsJson"></textarea>
                </div>
                <div class="form-group">
                    <label for="gs1DataMatrixJson" class="label">GS1 DataMatrix Object</label>
                    <textarea id="gs1DataMatrixJson" class="form-control jsonOptionsInput" rows="6"
                        v-model="gs1DataMatrixJson"></textarea>
                </div>
            </div>
            <div class="form-group">
                <label for="selectedDpiSelectOption" class="label">Barcode DPI</label><br />
                <select id="selectedDpiSelectOption" class="form-control" v-model="selectedDpiSelectOption">
                    <option v-for="dpiSelectOption in dpiSelectOptions" :value="dpiSelectOption.value">
                        {{ dpiSelectOption.name }}
                    </option>
                </select>
            </div>
            <div class="form-group">
                <label for="selectedDpiSelectOption" class="label">QRCode</label><br />
                <img :src="qrcodeurl" class="qrPreviewSmall" alt="QR Code preview" />
                <div v-if="qrEncodedValue" class="encodedValuePreview">{{ qrEncodedValue }}</div>
            </div>
            <div class="form-group" v-if="upc && gtinurl">
                <label class="label">GTIN Barcode</label><br />
                <img :src="gtinurl" alt="GTIN Barcode" />
            </div>
            <div class="form-group" v-if="upc && combinedbarcodeurl">
                <label class="label">Combined QR/GTIN</label><br />
                <img :src="combinedbarcodeurl" alt="Combined QR and GTIN Barcode" />
                <div v-if="qrEncodedValue" class="encodedValuePreview">{{ qrEncodedValue }}</div>
            </div>
            <div class="form-group" v-if="gs1qrcodeurl">
                <label class="label">GS1 QR Code</label><br />
                <img :src="gs1qrcodeurl" alt="GS1 QR Code" />
                <div v-if="gs1QrEncodedValue" class="encodedValuePreview">{{ gs1QrEncodedValue }}</div>
            </div>
            <div class="form-group" v-if="gs1datamatrixurl">
                <label class="label">GS1 DataMatrix</label><br />
                <img :src="gs1datamatrixurl" alt="GS1 DataMatrix" />
                <div v-if="gs1DataMatrixEncodedValue" class="encodedValuePreview">{{ gs1DataMatrixEncodedValue }}</div>
            </div>
            <div v-if="messageNote.length > 0" class="noteError">{{ messageNote }}</div>
            <div class="row">
                <button class="col-offset-2 col-3 btn btn-primary" :disabled="!redirectSafetyCanSubmit">Save Code</button>
            </div>
        </form>
    </div>
</template>

<script>
import { v4 as uuidv4, v6 as uuidv6 } from 'uuid';
import shortUuid from 'short-uuid';
import { component, ref, onUpdated } from 'vue';
import {
    DEFAULT_GS1_DIGITAL_LINK_BASE_URI,
    buildShortcodeBaseUri,
    buildGs1DigitalLinkUri,
    buildGs1ElementString,
    buildGs1FormAiEntries,
    decycle,
    getUser,
    getUserFullNameFromApi,
    getGs1DataMatrixUrlFromApi,
    getGs1QrUrlFromApi,
    getCodeUrlByShortcode,
    getCombinedBarcodeUrlFromApi,
    getGtinUrlFromApi,
    getQrCodeUrlFromApi,
    getSingleCodeFromApi,
    normalizeCodeFromApi,
    normalizeGs1AiEntries,
    normalizeGs1DigitalLinkBaseUri,
} from "../../libs/libs.js"
import { apiFetchJson } from "@/libs/api"
import { getUserId } from "@/libs/auth"
import { isValidHttpUrl } from "@/libs/safetyLookup"
import RedirectSafetyStatus from "@/components/RedirectSafetyStatus.vue"
import redirectSafetyValidation from "@/mixins/redirectSafetyValidation"
const uuidTranslator = shortUuid();
const IN_BROWSER = typeof window !== 'undefined'
const USER_AGENT = IN_BROWSER ? window.navigator?.userAgent : undefined
const IS_SAFARI = USER_AGENT?.includes('AppleWebKit') && !USER_AGENT?.includes('Chrome')

function calculateUPCCheckDigit(upc) {
    // Ensure the UPC is a string and remove any non-digit characters
    upc = upc.toString().replace(/\D/g, '');

    // Check if the UPC has the correct length (11 digits)
    if (upc.length !== 11) {
        return "Invalid UPC length";
    }

    // Calculate the check digit
    let sumOdd = 0;
    let sumEven = 0;

    for (let i = 0; i < upc.length; i++) {
        const digit = parseInt(upc[i]);

        if ((i + 1) % 2 === 1) {
            sumOdd += digit;
        } else {
            sumEven += digit;
        }
    }

    const total = sumOdd * 3 + sumEven;
    const checkDigit = (10 - (total % 10)) % 10;

    return checkDigit;
}

function isNumeric(str) {
    const num = Number(str);
    return !isNaN(num) && isFinite(num);
}

function sleep(ms) {
    return new Promise((resolve) => {
        setTimeout(resolve, ms);
    });
}

function validCheckDigit(upc) {
    if (!upc)
        return false;
    if (!isNumeric(upc))
        return false;
    if (typeof upc != 'string')
        return false;
    if (upc.length < 12)
        return false;
    let calcCheckDigit = calculateUPCCheckDigit(upc.substring(0, 11));
    let checkDigit = upc.substring(11, 12)
    if (calcCheckDigit != checkDigit)
        return false;
    return true;
}

export default {
    components: {
        RedirectSafetyStatus,
    },
    mixins: [redirectSafetyValidation],
    props: [
        "codeid"
    ],
    data() {
        return {
            id: '',
            authOwnerUserId: '',
            codeidQuery: '',//this.codeid,
            ownername: '',
            ownerusername: '',
            owneruserid: '',
            owneremail: '',
            BusName: '',
            codeDescription: '',
            desturl: '',
            phone: '',
            location: '',
            upc: '',
            upcSuggestion: '12345678901',
            mappedUpc: '',
            shortcodeid: '',
            sourceurl: '',
            redirecturl: '',
            calcCheckDigit: false,
            upcError: '',
            upcBarcode: '',
            qrCodeImageData: null,
            upcImageData: null,
            overlaidData: null,
            overlaidImageData: null,
            qrcodeurl: null,
            gtinurl: null,
            combinedbarcodeurl: null,
            gs1qrcodeurl: null,
            gs1datamatrixurl: null,
            dpiSelectOptions: [
                {
                    name: "Web (96dpi)",
                    value: 96
                },
                {
                    name: "Print (384dpi)",
                    value: 384
                },
            ],
            selectedDpiSelectOption: 96,
            selectedStyleSelectOption: 1,
            messageNote: [],
            upcSupport: false,
            gs1Support: false,
            displayUserStuffDetail: false,
            showAdvancedBarcodeOptions: false,
            useAdvancedBarcodeJson: false,
            qrOptionsJson: "",
            gtinOptionsJson: "",
            combinedBarcodeJson: "",
            gs1QrOptionsJson: "",
            gs1DataMatrixJson: "",
            gs1DigitalLinkBaseUri: DEFAULT_GS1_DIGITAL_LINK_BASE_URI,
            gs1AiEntries: buildGs1FormAiEntries(),
            gtinFormats: ["default", "ean13", "ean8", "upca", "upce", "code39", "code128", "itf14"],
            qrWidth: 600,
            qrColor: "000000",
            qrBackground: "FFFFFF",
            qrErrorCorrectionLevel: "M",
            qrBorder: 1,
            qrBaseurl: "",
            gtinFormat: "default",
            gtinWidth: 400,
            gtinHeight: 60,
            gtinScale: 3,
            gtinIncludeText: true,
            gtinTextsize: 12,
            gtinColor: "000000",
            gtinBackground: "FFFFFF",
            combinedFormat: "png",
            combinedBaseurl: "",
            combinedGtinWidth: 400,
            combinedGtinHeight: 60,
            combinedGtinOrientation: "horizontal",
            combinedGtinFormat: "default",
            combinedGtinScale: 3,
            combinedIncludeGtinText: true,
            combinedGtinColor: "000000",
            combinedGtinBackground: "FFFFFF",
            combinedQrSizeRatio: 0.25,
            combinedQrHorizontalRatio: 0.5,
            combinedQrVerticalRatio: 0.8,
            combinedQrColor: "000000",
            combinedQrBackground: "FFFFFF",
            combinedQrErrorCorrectionLevel: "M",
            combinedQrBorder: 1,
            gs1QrScale: 4,
            gs1QrIncludeText: false,
            gs1QrTextsize: 10,
            gs1QrColor: "000000",
            gs1QrBackground: "FFFFFF",
            gs1DataMatrixScale: 4,
            gs1DataMatrixIncludeText: false,
            gs1DataMatrixTextsize: 10,
            gs1DataMatrixColor: "000000",
            gs1DataMatrixBackground: "FFFFFF",
            gs1DataMatrixRectangular: false,
            gs1DigitalLinkBaseUriHelpText: "Leaving this blank is recommended. QR Code Experts will generate the right URL automatically so this code gets the platform's full feature set.",
        }
    },
    computed: {
        qrEncodedValue() {
            return this.shortcodeid ? buildShortcodeBaseUri(this.shortcodeid) : "";
        },
        derivedGs1AiEntries() {
            return this.resolveGs1AiEntries();
        },
        derivedGs1ElementString() {
            return buildGs1ElementString(this.derivedGs1AiEntries);
        },
        derivedGs1DigitalLinkUri() {
            const resolvedGs1DigitalLinkBaseUri = `${this.gs1DigitalLinkBaseUri ?? ""}`.trim() ||
                buildShortcodeBaseUri(this.shortcodeid);
            return buildGs1DigitalLinkUri(
                this.derivedGs1AiEntries,
                resolvedGs1DigitalLinkBaseUri
            );
        },
        gs1QrEncodedValue() {
            return this.derivedGs1DigitalLinkUri || this.code?.gs1?.digitalLinkUri || "";
        },
        gs1DataMatrixEncodedValue() {
            return this.derivedGs1ElementString || this.code?.gs1?.elementString || "";
        },
    },
    methods: {
        handleDisplayUserStuffDetail() {
            this.displayUserStuffDetail = !this.displayUserStuffDetail
        },
        toggleAdvancedBarcodeOptions() {
            this.showAdvancedBarcodeOptions = !this.showAdvancedBarcodeOptions
        },
        formatHexColor(value) {
            const raw = `${value ?? ""}`.trim().replace(/^#/, "");
            if (!/^[0-9a-fA-F]{6}$/.test(raw)) {
                return "#000000";
            }
            return `#${raw.toUpperCase()}`;
        },
        updateColorField(fieldName, value) {
            this[fieldName] = `${value ?? ""}`.trim().replace(/^#/, "").toUpperCase();
        },
        addGs1AiEntry() {
            this.gs1AiEntries.push({ ai: "", value: "", isDefault: false });
        },
        removeGs1AiEntry(index) {
            const entry = this.gs1AiEntries[index];
            if (!entry || entry.isDefault) return;
            this.gs1AiEntries.splice(index, 1);
        },
        syncGs1Ai01FromGtin() {
            if (!(this.gs1Support && this.upcSupport && validCheckDigit(this.upc))) return;
            const gtinEntry = this.gs1AiEntries.find((entry) => `${entry?.ai ?? ""}`.trim() === "01");
            if (!gtinEntry) return;
            gtinEntry.value = `${this.upc}`.padStart(14, "0");
        },
        resolveGs1AiEntries() {
            const entries = normalizeGs1AiEntries(this.gs1AiEntries).map((entry) => ({ ...entry }));
            const gtinEntry = entries.find((entry) => entry.ai === "01");
            if (gtinEntry && gtinEntry.value.length === 0 && this.gs1Support && this.upcSupport && validCheckDigit(this.upc)) {
                gtinEntry.value = `${this.upc}`.padStart(14, "0");
            }
            return entries;
        },
        jsonPretty(val, indent = 2) {
            if (typeof val === "string") {
                val = JSON.parse(val)
            }
            return JSON.stringify(val, null, indent)
        },
        checkUPC(evt) {
            let res
            //console.log(evt)
            if (!isNumeric(this.upc)) {
                this.upcError = "UPC must be a number"
                this.mappedUpc = ''
            }
            if (isNumeric(this.upc) && this.upc.length < 11) {
                this.upcError = "UPC must be at least 11 digits long"
                this.mappedUpc = ''
            }
            if (isNumeric(this.upc) && this.upc.length == 11) {
                res = calculateUPCCheckDigit(this.upc)
                this.upcError = `UPC with check digit - ${res} - ${this.upc}${res}`
                this.mappedUpc = ''
            }
            if (isNumeric(this.upc) && this.upc.length == 11 && this.calcCheckDigit) {
                res = calculateUPCCheckDigit(this.upc)
                this.upc = this.upc + res
                this.upcError = ''
                this.mappedUpc = this.upc
            }
            if (this.upc.length == 12) {
                let check = this.upc[11];
                let correctCheck = calculateUPCCheckDigit(this.upc.substring(0, 11));
                if (check != correctCheck) {
                    this.upcError = `Check digit invalid. Barcode will not generate. Check digit should be ${correctCheck}`
                } else {
                    this.upcError = ''
                    this.mappedUpc = this.upc
                }
            }
            if (this.upc.length > 12) {
                this.upcError = `UPC value has too many digits ${this.upc.length}`
            }

        },
        handleSubmit() {
            this.messageNote = [];
            if (!this.redirectSafetyCanSubmit) {
                this.messageNote.push("Redirect URL must pass safety lookup before saving.");
                return;
            }
            const gs1Enabled = this.upcSupport && this.gs1Support;
            const rawGs1DigitalLinkBaseUri = `${this.gs1DigitalLinkBaseUri ?? ""}`.trim();
            if (gs1Enabled && rawGs1DigitalLinkBaseUri && !isValidHttpUrl(rawGs1DigitalLinkBaseUri)) {
                this.messageNote.push("GS1 Digital Link base URI must be valid.");
                return;
            }
            const normalizedGs1DigitalLinkBaseUri = rawGs1DigitalLinkBaseUri ||
                buildShortcodeBaseUri(this.shortcodeid);
            const gs1AiEntries = this.resolveGs1AiEntries();
            const populatedGs1AiEntries = gs1AiEntries.filter((entry) => entry.value.length > 0);
            const duplicateAis = populatedGs1AiEntries
                .map((entry) => entry.ai)
                .filter((ai, index, list) => list.indexOf(ai) !== index);
            if (gs1Enabled && duplicateAis.length > 0) {
                this.messageNote.push(`GS1 AIs must be unique. Duplicate AI: ${duplicateAis[0]}`);
                return;
            }
            if (gs1Enabled && populatedGs1AiEntries.some((entry) => !/^\d{2,4}$/.test(entry.ai))) {
                this.messageNote.push("GS1 AIs must be 2 to 4 digits.");
                return;
            }
            const ai01Entry = populatedGs1AiEntries.find((entry) => entry.ai === "01");
            if (ai01Entry && !/^\d{14}$/.test(ai01Entry.value)) {
                this.messageNote.push("AI 01 must be a 14-digit GTIN.");
                return;
            }
            const ai17Entry = populatedGs1AiEntries.find((entry) => entry.ai === "17");
            if (ai17Entry && !/^\d{6}$/.test(ai17Entry.value)) {
                this.messageNote.push("AI 17 must use YYMMDD format.");
                return;
            }
            if (gs1Enabled && populatedGs1AiEntries.length > 0 && !ai01Entry) {
                this.messageNote.push("AI 01 (GTIN) is required for GS1 Digital Link output.");
                return;
            }
            const now = new Date();
            const utcDate = now.toISOString();
            let code = {
                userid: this.owneruserid,
                title: this.BusName,
                description: this.codeDescription || this.BusName,
                redirectUrl: null,
                phone: null,
                address: null,
                gtin: null,
                autoFixGtinCheckDigit: false,
                metadata: {
                    selectedDpiSelectOption: this.selectedDpiSelectOption,
                    selectedStyleSelectOption: this.selectedStyleSelectOption,
                    sourceurl: this.sourceurl,
                    upcSupport: this.upcSupport,
                    gs1Support: this.gs1Support,
                },
            }

            let allOK = true;
            if (!this.BusName) {
                this.messageNote.push("Business Name / Business Message Required")
                allOK = false;
            } else {
                code.title = this.BusName;
            }
            code.description = this.codeDescription || this.BusName;
            if (this.selectedStyleSelectOption == 1) {
                let optionOne = this.redirecturl ? true : false
                if (!optionOne) {
                    this.messageNote.push("For Redirect Only Style then redirect URL must be provided")
                    allOK = false
                } else {
                    if (!isValidHttpUrl(this.redirecturl)) {
                        this.messageNote.push("Redirect URL must be valid")
                        allOK = false
                    } else {
                        code.redirectUrl = this.redirecturl
                    }
                }
            }
            if (this.selectedStyleSelectOption == 2) {
                let optionTwo = this.desturl || this.location || this.phone
                if (!optionTwo) {
                    this.messageNote.push("For Traditional Style one of these must be populated: Web URL, Location, or Phone.")
                    allOK = false
                } else {
                    if (!isValidHttpUrl(this.desturl)) {
                        this.messageNote.push("Web URL must be valid.")
                        allOK = false
                    } else {
                        if (this.desturl) {
                            code.redirectUrl = this.desturl
                        }
                        if (this.location) {
                            code.address = this.location
                        }
                        if (this.phone) {
                            code.phone = this.phone
                        }
                    }
                }
            }
            if (this.upcSupport) {
                if (!this.upc) {
                    this.messageNote.push("If UPC Support, then UPC must be provided.")
                    allOK = false;
                } else {
                    if (this.upc.length == 11) {
                        let res = correctCheck = calculateUPCCheckDigit(this.upc)
                        this.messageNote.push(`If UPC Support, then UPC must be valid. Check digit not supplied. Check digit should be ${res}`)
                        allOK = false
                    } else
                        if (!validCheckDigit(this.upc)) {
                            this.messageNote.push("If UPC Support, then UPC must be valid")
                            allOK = false
                        } else {
                            code.metadata.upcSupport = this.upcSupport
                            code.gtin = this.upc
                            code.autoFixGtinCheckDigit = this.calcCheckDigit
                        }
                }
            }
            code.qr = {
                width: this.qrWidth,
                color: this.qrColor,
                background: this.qrBackground,
                errorCorrectionLevel: this.qrErrorCorrectionLevel,
                border: this.qrBorder,
                baseurl: this.qrBaseurl,
            };
            code.gtinOptions = {
                format: this.gtinFormat,
                width: this.gtinWidth,
                height: this.gtinHeight,
                scale: this.gtinScale,
                includetext: this.gtinIncludeText,
                textsize: this.gtinTextsize,
                color: this.gtinColor,
                background: this.gtinBackground,
            };
            code.combinedBarcode = {
                gtinWidth: this.combinedGtinWidth,
                gtinHeight: this.combinedGtinHeight,
                gtinOrientation: this.combinedGtinOrientation,
                gtinFormat: this.combinedGtinFormat,
                gtinScale: this.combinedGtinScale,
                includeGtinText: this.combinedIncludeGtinText,
                gtinColor: this.combinedGtinColor,
                gtinBackground: this.combinedGtinBackground,
                qrSizeRatio: this.combinedQrSizeRatio,
                qrHorizontalRatio: this.combinedQrHorizontalRatio,
                qrVerticalRatio: this.combinedQrVerticalRatio,
                qrColor: this.combinedQrColor,
                qrBackground: this.combinedQrBackground,
                qrErrorCorrectionLevel: this.combinedQrErrorCorrectionLevel,
                qrBorder: this.combinedQrBorder,
                format: this.combinedFormat,
                baseurl: this.combinedBaseurl,
            };
            if (gs1Enabled && populatedGs1AiEntries.length > 0) {
                code.gs1 = {
                    aiEntries: populatedGs1AiEntries.map(({ ai, value }) => ({ ai, value })),
                    digitalLinkBaseUri: normalizedGs1DigitalLinkBaseUri,
                    digitalLinkUri: buildGs1DigitalLinkUri(populatedGs1AiEntries, normalizedGs1DigitalLinkBaseUri) || undefined,
                    elementString: buildGs1ElementString(populatedGs1AiEntries) || undefined,
                };
            }
            if (gs1Enabled && populatedGs1AiEntries.length > 0) {
                code.gs1Qr = {
                    scale: this.gs1QrScale,
                    includetext: this.gs1QrIncludeText,
                    textsize: this.gs1QrTextsize,
                    color: this.gs1QrColor,
                    background: this.gs1QrBackground,
                };
            }
            if (gs1Enabled && populatedGs1AiEntries.length > 0) {
                code.gs1DataMatrix = {
                    scale: this.gs1DataMatrixScale,
                    includetext: this.gs1DataMatrixIncludeText,
                    textsize: this.gs1DataMatrixTextsize,
                    color: this.gs1DataMatrixColor,
                    background: this.gs1DataMatrixBackground,
                    rectangular: this.gs1DataMatrixRectangular,
                };
            }
            if (this.useAdvancedBarcodeJson) {
                const parseOptionJson = (label, value) => {
                    if (!value || value.trim().length === 0) {
                        return null;
                    }
                    try {
                        const parsed = JSON.parse(value);
                        if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
                            throw new Error("must be a JSON object");
                        }
                        return parsed;
                    } catch (err) {
                        const errMsg = err && err.message ? err.message : "invalid JSON";
                        this.messageNote.push(`${label} invalid: ${errMsg}`);
                        allOK = false;
                        return null;
                    }
                };
                const parsedQr = parseOptionJson("QR options", this.qrOptionsJson);
                const parsedGtinOptions = parseOptionJson("GTIN options", this.gtinOptionsJson);
                const parsedCombinedBarcode = parseOptionJson("Combined barcode options", this.combinedBarcodeJson);
                const parsedGs1Qr = parseOptionJson("GS1 QR options", this.gs1QrOptionsJson);
                const parsedGs1DataMatrix = parseOptionJson("GS1 DataMatrix options", this.gs1DataMatrixJson);
                if (parsedQr) {
                    code.qr = parsedQr;
                }
                if (parsedGtinOptions) {
                    code.gtinOptions = parsedGtinOptions;
                }
                if (parsedCombinedBarcode) {
                    code.combinedBarcode = parsedCombinedBarcode;
                }
                if (parsedGs1Qr) {
                    code.gs1Qr = parsedGs1Qr;
                }
                if (parsedGs1DataMatrix) {
                    code.gs1DataMatrix = parsedGs1DataMatrix;
                }
            }
            if (allOK) {
                let url = getCodeUrlByShortcode(this.codeidQuery)
                apiFetchJson(url, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(code),
                })
                    .then((newcode) => {
                        console.log(`handleSubmit response ${JSON.stringify(newcode)}`)
                        this.$router.push(`/code/${newcode.shortcode || this.codeidQuery}`)
                    })
                    .catch((err) => {
                        this.messageNote.push("Unable to save code")
                        console.log(`handleSubmit fetch failed ${JSON.stringify(err)}`)
                    })
            }
        },

    },
    mounted() {
        this.authOwnerUserId = getUserId()
        if (!this.authOwnerUserId) {
            this.$router.push({ path: "/auth" })
            return
        }
        this.codeidQuery = this.codeid //$router.params.codeid
        let url = getCodeUrlByShortcode(this.codeidQuery)
        apiFetchJson(url, {}, { auth: true })
            .then(data => {
                this.code = normalizeCodeFromApi(getSingleCodeFromApi(data))
            })
            .then(() => {
                console.log(`code loaded ${JSON.stringify(this.code)}`)
                //assign local data from api call
                this.id = this.code.id
                this.owneruserid = this.code.owneruserid
                //this.codeid = this.code.codeid
                this.shortcodeid = this.code.shortcodeid
                this.sourceurl = this.code.sourceurl
                this.BusName = this.code.BusName
                this.codeDescription = this.code.description || this.code.BusName || ''
                this.selectedDpiSelectOption = this.code.selectedDpiSelectOption
                this.selectedStyleSelectOption = 1
                this.codeStyle = 1
                this.desturl = this.code.desturl
                this.phone = this.code.phone
                this.location = this.code.location
                this.redirecturl = this.code.redirecturl
                this.gs1Support = Boolean(this.code.gs1Support)
                this.upcSupport = this.code.upcSupport || this.gs1Support
                this.upc = this.code.upc
                this.calcCheckDigit = this.code.calcCheckDigit
                this.gs1DigitalLinkBaseUri = `${this.code.gs1?.digitalLinkBaseUri ?? ""}`.trim() ||
                    buildShortcodeBaseUri(this.code.shortcode || this.code.shortcodeid)
                this.gs1AiEntries = buildGs1FormAiEntries(this.code)
                this.qrWidth = this.code.qr?.width ?? this.qrWidth
                this.qrColor = this.code.qr?.color ?? this.qrColor
                this.qrBackground = this.code.qr?.background ?? this.qrBackground
                this.qrErrorCorrectionLevel = this.code.qr?.errorCorrectionLevel ?? this.qrErrorCorrectionLevel
                this.qrBorder = this.code.qr?.border ?? this.qrBorder
                this.qrBaseurl = this.code.qr?.baseurl ?? this.code.qr?.baseUrl ?? this.qrBaseurl
                this.gtinFormat = this.code.gtinOptions?.format ?? this.gtinFormat
                this.gtinWidth = this.code.gtinOptions?.width ?? this.gtinWidth
                this.gtinHeight = this.code.gtinOptions?.height ?? this.gtinHeight
                this.gtinScale = this.code.gtinOptions?.scale ?? this.gtinScale
                this.gtinIncludeText = this.code.gtinOptions?.includetext ?? this.gtinIncludeText
                this.gtinTextsize = this.code.gtinOptions?.textsize ?? this.gtinTextsize
                this.gtinColor = this.code.gtinOptions?.color ?? this.gtinColor
                this.gtinBackground = this.code.gtinOptions?.background ?? this.gtinBackground
                this.combinedFormat = this.code.combinedBarcode?.format ?? this.combinedFormat
                this.combinedBaseurl = this.code.combinedBarcode?.baseurl ?? this.code.combinedBarcode?.baseUrl ?? this.combinedBaseurl
                this.combinedGtinWidth = this.code.combinedBarcode?.gtinWidth ?? this.combinedGtinWidth
                this.combinedGtinHeight = this.code.combinedBarcode?.gtinHeight ?? this.combinedGtinHeight
                this.combinedGtinOrientation = this.code.combinedBarcode?.gtinOrientation ?? this.combinedGtinOrientation
                this.combinedGtinFormat = this.code.combinedBarcode?.gtinFormat ?? this.combinedGtinFormat
                this.combinedGtinScale = this.code.combinedBarcode?.gtinScale ?? this.combinedGtinScale
                this.combinedIncludeGtinText = this.code.combinedBarcode?.includeGtinText ?? this.combinedIncludeGtinText
                this.combinedGtinColor = this.code.combinedBarcode?.gtinColor ?? this.combinedGtinColor
                this.combinedGtinBackground = this.code.combinedBarcode?.gtinBackground ?? this.combinedGtinBackground
                this.combinedQrSizeRatio = this.code.combinedBarcode?.qrSizeRatio ?? this.combinedQrSizeRatio
                this.combinedQrHorizontalRatio = this.code.combinedBarcode?.qrHorizontalRatio ?? this.combinedQrHorizontalRatio
                this.combinedQrVerticalRatio = this.code.combinedBarcode?.qrVerticalRatio ?? this.combinedQrVerticalRatio
                this.combinedQrColor = this.code.combinedBarcode?.qrColor ?? this.combinedQrColor
                this.combinedQrBackground = this.code.combinedBarcode?.qrBackground ?? this.combinedQrBackground
                this.combinedQrErrorCorrectionLevel = this.code.combinedBarcode?.qrErrorCorrectionLevel ?? this.combinedQrErrorCorrectionLevel
                this.combinedQrBorder = this.code.combinedBarcode?.qrBorder ?? this.combinedQrBorder
                this.gs1QrScale = this.code.gs1Qr?.scale ?? this.gs1QrScale
                this.gs1QrIncludeText = this.code.gs1Qr?.includetext ?? this.gs1QrIncludeText
                this.gs1QrTextsize = this.code.gs1Qr?.textsize ?? this.gs1QrTextsize
                this.gs1QrColor = this.code.gs1Qr?.color ?? this.gs1QrColor
                this.gs1QrBackground = this.code.gs1Qr?.background ?? this.gs1QrBackground
                this.gs1DataMatrixScale = this.code.gs1DataMatrix?.scale ?? this.gs1DataMatrixScale
                this.gs1DataMatrixIncludeText = this.code.gs1DataMatrix?.includetext ?? this.gs1DataMatrixIncludeText
                this.gs1DataMatrixTextsize = this.code.gs1DataMatrix?.textsize ?? this.gs1DataMatrixTextsize
                this.gs1DataMatrixColor = this.code.gs1DataMatrix?.color ?? this.gs1DataMatrixColor
                this.gs1DataMatrixBackground = this.code.gs1DataMatrix?.background ?? this.gs1DataMatrixBackground
                this.gs1DataMatrixRectangular = this.code.gs1DataMatrix?.rectangular ?? this.gs1DataMatrixRectangular
                this.qrOptionsJson = this.code.qr ? JSON.stringify(this.code.qr, null, 2) : ""
                this.gtinOptionsJson = this.code.gtinOptions ? JSON.stringify(this.code.gtinOptions, null, 2) : ""
                this.combinedBarcodeJson = this.code.combinedBarcode ? JSON.stringify(this.code.combinedBarcode, null, 2) : ""
                this.gs1QrOptionsJson = this.code.gs1Qr ? JSON.stringify(this.code.gs1Qr, null, 2) : ""
                this.gs1DataMatrixJson = this.code.gs1DataMatrix ? JSON.stringify(this.code.gs1DataMatrix, null, 2) : ""
                this.qrcodeurl = getQrCodeUrlFromApi(this.code)
                if (this.upc) {
                    this.gtinurl = getGtinUrlFromApi(this.code)
                    this.combinedbarcodeurl = getCombinedBarcodeUrlFromApi(this.code)
                }
                this.gs1qrcodeurl = getGs1QrUrlFromApi(this.code)
                this.gs1datamatrixurl = getGs1DataMatrixUrlFromApi(this.code)
            })
            .then(async () => {
                console.log(`code owneruserid ${this.code.owneruserid}`)
                this.user = await getUser(this.code.owneruserid)
                this.owneruserid = this.user.id
                this.ownerusername = this.user.username
                this.ownername = getUserFullNameFromApi(this.user)
            })
            .catch(err => {
                console.log(err.message)
            })
    },
    onChange() {
        console.log(`This is a test.... onChange`)
    },
    setup() {
    }

}
</script>

<style>
.fatfinger {
    margin-top: 25px;
    margin-bottom: 25px;
}

input.form-control,
select.form-control,
textarea.form-control {
    background: var(--qrce-surface-2);
    border: 1px solid rgba(15, 23, 42, 0.22);
    border-radius: 12px;
    color: var(--qrce-text);
    padding: 10px 12px;
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.7);
}

input.form-control:hover,
select.form-control:hover,
textarea.form-control:hover {
    border-color: rgba(15, 23, 42, 0.32);
}

input.form-control::placeholder,
textarea.form-control::placeholder {
    color: rgba(15, 23, 42, 0.55);
}


.xForm {
    max-width: 586px;
    margin: 20px auto;
    background: var(--qrce-surface);
    text-align: left;
    padding: 20px;
    border-radius: var(--qrce-radius);
    border: 1px solid var(--qrce-border);
    border-left: 4px solid var(--qrce-primary);
    box-shadow: 0 10px 22px rgba(15, 23, 42, 0.10);
    box-sizing: border-box;
}

@media (max-width: 819px) {
    .xForm {
        width: calc(100% - 24px);
        margin: 14px 12px;
    }
}

.formSectionLabel {
    font-size: 0.9em;
    text-transform: uppercase;
    letter-spacing: 1px;
    font-weight: bold;
}

.form-control {
    text-align: left;
    margin-top: 5px;
    display: block;
    width: 100%;
}

.jsonOptionsInput {
    font-family: monospace;
}

.optionsGrid {
    display: grid;
    grid-template-columns: repeat(2, minmax(180px, 1fr));
    gap: 12px;
}

.labelWithHelp {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
}

.helpLink {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    border-radius: 999px;
    border: 1px solid var(--qrce-border);
    color: var(--qrce-primary);
    text-decoration: none;
    font-weight: 700;
    background: var(--qrce-surface);
}

.helpLink:hover {
    text-decoration: none;
    border-color: var(--qrce-primary);
}

.gs1AiRow {
    display: grid;
    grid-template-columns: 90px minmax(0, 1fr) auto;
    gap: 10px;
    align-items: center;
    margin-bottom: 10px;
}

.gs1AiCode {
    text-align: center;
}

.gs1AiValue {
    min-width: 0;
}

.colorPicker {
    height: 40px;
    padding: 2px;
}

.label {
    font-size: 0.8em;
    text-transform: uppercase;
    letter-spacing: 1px;
    font-weight: bold;
}

.fieldPreview {
    overflow-wrap: anywhere;
    word-break: break-word;
    color: var(--qrce-muted);
    font-size: 0.95em;
    margin: 0 0 6px;
}

.qrPreviewSmall {
    width: 60%;
    max-width: 100%;
    height: auto;
}

.encodedValuePreview {
    margin-top: 10px;
    overflow-wrap: anywhere;
    word-break: break-word;
    color: var(--qrce-muted);
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
    font-size: 0.92rem;
    line-height: 1.4;
}

.form-control label {
    order: 1;
    color: #232323;
    /*display: inline-block;*/
    margin: 5px 0 1px;
    font-size: 0.8em;
    text-transform: uppercase;
    letter-spacing: 1px;
    font-weight: bold;
}

.form-control input {
    order: 2;
    /*display: block;*/
    margin: 5px 0 5px;
    padding: 10px 6px;
    width: 100%;
    box-sizing: border-box;
    border: 0;
    border-bottom: 1px solid #ddd;
    color: #555;
}

.form-control select {
    order: 2;
    /*display: block;*/
    margin: 5px 0 5px;
    padding: 10px 6px;
    width: 100%;
    box-sizing: border-box;
    border: 0;
    border-bottom: 1px solid #ddd;
    color: #555;
}

.form-control input[type="checkbox"] {
    order: 1;
    display: inline-block;
    width: 16px;
    margin: 5px 10px 5px 5px;
    position: relative;
    top: 2px;
}

div.notes {
    order: 99;
    margin-left: 10px;
}

div.upcError {
    order: 99;
    color: red;
    margin-left: 10px;
}

div.noteError {
    color: red;
    margin-left: 10px;
    margin-bottom: 20px;
}

.btn:disabled {
    opacity: 0.55;
    cursor: not-allowed;
}

input[required]+label:after {
    content: '*';
    color: red;
}

.thisOr {
    border: 3px solid blue;
    padding: 10px;
    margin: 10px;
}

textarea {
    border: 1px solid #ddd;
    padding: 10px;
    width: 100%;
    box-sizing: border-box;
    height: 100px;
}

.btn-primary {
    display: block;
    margin: 20px auto 0;
    background: rgb(46, 108, 183);
    color: white;
    padding: 10px;
    border: 0;
    border-radius: 6px;
    font-size: 16px;
}

#qrcode {
    width: 50px;
    height: 50px;
}

.qrCodeImage {
    display: inline-block;
    border: 2px red;
    padding: 5px;
    margin: 5px;
    width: 100px;
    height: 100px;
    /*display: none;*/
}

.upcImage {
    display: inline-block;
    border: 2px green;
    padding: 5px;
    margin: 5px;
    /*width: 242px;
    height: 142px;*/
    /*display: none;*/
}

.overlaidImage {
    display: inline-block;
    width: 484px;
    height: 284px;
    border: 2px blue;
    padding: 5px;
    margin: 5px;
}

.overlaidImageCanvas {
    display: inline-block;
    width: 484px;
    height: 284px;
    border: 2px rgb(255, 0, 230);
    padding: 5px;
    margin: 5px;
}
</style>
